// /admin/compliance — operator console for staff certifications.
//
// Surfaces compliance state in one place: KPIs (valid / expiring / expired
// staff), filters (cert type, status, search), per-staff drawer with the
// full cert list and admin actions: add, edit, upload doc, revoke.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { ShieldCheck, X, Plus, Trash, FileArrowUp, FileText, ArrowsClockwise, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  STATUS_META,
  daysUntilExpiry,
  deriveStatus,
  deleteCertDocument,
  getCertDocumentUrl,
  listAllCertificationsForOrg,
  listCertTypes,
  revokeCertification,
  rollupByProfile,
  suggestExpiryDate,
  upsertCertification,
  uploadCertDocument,
} from "@/lib/compliance";
import { listStaff } from "@/lib/admin/staff";
import AdminChildHealthSection from "@/pages/admin/AdminChildHealthSection";
import AdminComplianceTodayTab from "@/pages/admin/AdminComplianceTodayTab";
import AdminComplianceCatalogTab from "@/pages/admin/AdminComplianceCatalogTab";

const FILTER_OPTIONS = [
  { value: "all",            label: "All staff" },
  { value: "expiring_soon",  label: "Expiring soon" },
  { value: "expired",        label: "Expired" },
  { value: "valid",          label: "Fully compliant" },
];

export default function AdminCompliance() {
  const { profile } = useAuth();
  const orgId = profile?.organization_id;
  const [staff, setStaff] = useState([]);
  const [certs, setCerts] = useState([]);
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [drawer, setDrawer] = useState({ open: false, profile: null });
  const [editor, setEditor] = useState({ open: false, cert: null, profile: null });
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (() => {
    const t = searchParams.get("tab");
    return ["today", "staff", "children", "catalog"].includes(t) ? t : "staff";
  })();
  const [tab, setTabState] = useState(initialTab);
  // Keep the URL in sync so Settings deep-links + browser-back work cleanly.
  // Each user click pushes a new history entry — back returns to the prior tab.
  const setTab = (next) => {
    setTabState(next);
    const sp = new URLSearchParams(searchParams);
    if (next === "staff") sp.delete("tab");
    else sp.set("tab", next);
    setSearchParams(sp);
  };

  const refresh = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const [s, c, t] = await Promise.all([
        listStaff({ organizationId: orgId }),
        listAllCertificationsForOrg(orgId),
        listCertTypes(orgId),
      ]);
      setStaff(s || []);
      setCerts(c || []);
      setTypes(t || []);
    } catch (err) {
      toast.error(err.message || "Could not load compliance data");
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { refresh(); }, [refresh]);

  // Build per-staff rollup, even for staff with zero certs.
  const rollups = useMemo(() => {
    const fromCerts = rollupByProfile(certs);
    const seen = new Set(fromCerts.map((r) => r.profile?.id));
    // Add staff who have zero cert rows yet.
    for (const s of staff) {
      if (!seen.has(s.profile.id)) {
        fromCerts.push({
          profile: s.profile,
          certs: [],
          counts: { valid: 0, expiring_soon: 0, expired: 0, revoked: 0 },
          worst: "valid",
        });
      }
    }
    // Filter to teachers + admins (skip cms_editor for the operations view).
    const teacherOrAdminIds = new Set(
      staff.filter((s) => s.roles.some((r) => r === "teacher" || r === "admin")).map((s) => s.profile.id),
    );
    return fromCerts.filter((r) => teacherOrAdminIds.has(r.profile?.id));
  }, [certs, staff]);

  const filtered = useMemo(() => {
    return rollups.filter((r) => {
      if (filter !== "all") {
        if (filter === "valid" && (r.counts.expired > 0 || r.counts.expiring_soon > 0)) return false;
        if (filter === "expiring_soon" && r.counts.expiring_soon === 0) return false;
        if (filter === "expired" && r.counts.expired === 0) return false;
      }
      if (search) {
        const s = search.toLowerCase();
        const name = (r.profile?.full_name || r.profile?.email || "").toLowerCase();
        if (!name.includes(s)) return false;
      }
      return true;
    });
  }, [rollups, filter, search]);

  const kpis = useMemo(() => ({
    total: rollups.length,
    expiring: rollups.filter((r) => r.counts.expiring_soon > 0).length,
    expired: rollups.filter((r) => r.counts.expired > 0).length,
    fully_compliant: rollups.filter((r) => r.counts.expired === 0 && r.counts.expiring_soon === 0 && r.certs.length > 0).length,
  }), [rollups]);

  const onOpenDrawer = (rollup) => setDrawer({ open: true, profile: rollup.profile });
  const onAddCert = (profileRow) => setEditor({ open: true, cert: null, profile: profileRow });
  const onEditCert = (cert, profileRow) => setEditor({ open: true, cert, profile: profileRow });

  return (
    <div data-testid="admin-compliance" className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">Trust & safety</p>
          <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">Compliance</h1>
          <p className="mt-1 max-w-xl text-sm text-neutral-600">
            {tab === "staff"
              ? "Track required certifications across your team. Reminders to staff fire automatically at 30 / 14 / 7 / 1 day before expiry plus the day of, and you receive a Monday digest."
              : "Track required health and immunization records for every child. Reminders are sent to each family's primary contact on the same calm cadence."}
          </p>
        </div>
        {tab === "staff" && (
          <button
            type="button"
            data-testid="admin-compliance-refresh"
            onClick={refresh}
            className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
          >
            <ArrowsClockwise size={12} weight="bold" />
            Refresh
          </button>
        )}
      </div>

      <nav
        role="tablist"
        aria-label="Compliance scope"
        className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white p-1 text-[12px] font-semibold"
      >
        <button
          role="tab"
          aria-selected={tab === "today"}
          data-testid="admin-compliance-tab-today"
          onClick={() => setTab("today")}
          className={`rounded-full px-3 py-1 transition ${tab === "today" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
        >
          Today
        </button>
        <button
          role="tab"
          aria-selected={tab === "staff"}
          data-testid="admin-compliance-tab-staff"
          onClick={() => setTab("staff")}
          className={`rounded-full px-3 py-1 transition ${tab === "staff" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
        >
          Staff
        </button>
        <button
          role="tab"
          aria-selected={tab === "children"}
          data-testid="admin-compliance-tab-children"
          onClick={() => setTab("children")}
          className={`rounded-full px-3 py-1 transition ${tab === "children" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
        >
          Children
        </button>
        <button
          role="tab"
          aria-selected={tab === "catalog"}
          data-testid="admin-compliance-tab-catalog"
          onClick={() => setTab("catalog")}
          className={`rounded-full px-3 py-1 transition ${tab === "catalog" ? "bg-primary text-white" : "text-neutral-700 hover:bg-neutral-50"}`}
        >
          Catalog
        </button>
      </nav>

      {tab === "today" && <AdminComplianceTodayTab />}
      {tab === "children" && <AdminChildHealthSection organizationId={orgId} />}
      {tab === "catalog" && <AdminComplianceCatalogTab />}
      {tab === "staff" && (
      <>
      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testId="admin-compliance-kpi-total" label="Staff tracked" value={kpis.total} />
        <Kpi testId="admin-compliance-kpi-fully-compliant" label="Fully compliant" value={kpis.fully_compliant} tone="ok" />
        <Kpi testId="admin-compliance-kpi-expiring" label="Has expiring soon" value={kpis.expiring} tone="warn" />
        <Kpi testId="admin-compliance-kpi-expired" label="Has expired" value={kpis.expired} tone="alert" />
      </section>

      <section className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-2">
          {FILTER_OPTIONS.map((o) => {
            const active = filter === o.value;
            return (
              <button
                key={o.value}
                type="button"
                data-testid={`admin-compliance-filter-${o.value}`}
                onClick={() => setFilter(o.value)}
                aria-pressed={active}
                className={`rounded-full px-3 py-1 text-[11px] font-semibold transition ${
                  active ? "bg-primary text-white" : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                }`}
              >
                {o.label}
              </button>
            );
          })}
          <div className="relative ml-auto w-full sm:w-64">
            <MagnifyingGlass size={12} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden />
            <input
              type="text"
              data-testid="admin-compliance-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search staff…"
              className="w-full rounded-full border border-neutral-200 bg-white px-9 py-1.5 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </section>

      {loading && <p className="text-sm text-neutral-500">Loading…</p>}
      {!loading && filtered.length === 0 && (
        <p data-testid="admin-compliance-empty" className="rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-6 text-center text-sm text-neutral-500">
          No staff match the current filter.
        </p>
      )}

      <ul className="space-y-2" data-testid="admin-compliance-list">
        {filtered.map((r) => (
          <li
            key={r.profile.id}
            data-testid={`admin-compliance-row-${r.profile.id}`}
            data-worst={r.worst}
            className="flex flex-wrap items-center gap-3 rounded-2xl border border-neutral-200 bg-white p-3"
          >
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-neutral-900">{r.profile.full_name || r.profile.email}</p>
              <p className="truncate text-[11px] text-neutral-500">{r.profile.email}</p>
            </div>
            <div className="flex flex-wrap gap-1.5 text-[10px] font-bold uppercase tracking-wider">
              {r.certs.length === 0 && (
                <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2 py-0.5 text-neutral-600">
                  No records
                </span>
              )}
              {r.counts.valid > 0 && <Pill tone="ok">{r.counts.valid} valid</Pill>}
              {r.counts.expiring_soon > 0 && <Pill tone="warn">{r.counts.expiring_soon} expiring</Pill>}
              {r.counts.expired > 0 && <Pill tone="alert">{r.counts.expired} expired</Pill>}
              {r.counts.revoked > 0 && <Pill tone="muted">{r.counts.revoked} revoked</Pill>}
            </div>
            <button
              type="button"
              data-testid={`admin-compliance-open-${r.profile.id}`}
              onClick={() => onOpenDrawer(r)}
              className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Manage
            </button>
          </li>
        ))}
      </ul>

      <ProfileDrawer
        open={drawer.open}
        onOpenChange={(o) => setDrawer((s) => ({ ...s, open: o }))}
        profile={drawer.profile}
        types={types}
        certs={certs.filter((c) => c.profile_id === drawer.profile?.id)}
        onAdd={() => onAddCert(drawer.profile)}
        onEdit={(c) => onEditCert(c, drawer.profile)}
        onChanged={refresh}
      />

      <CertEditorSheet
        open={editor.open}
        onOpenChange={(o) => setEditor((s) => ({ ...s, open: o }))}
        cert={editor.cert}
        profile={editor.profile}
        types={types}
        onSaved={refresh}
      />
      </>
      )}
    </div>
  );
}

function Kpi({ testId, label, value, tone }) {
  const toneClass = {
    ok:    "border-emerald-200",
    warn:  "border-amber-300",
    alert: "border-[#C6851E]/40",
  }[tone || ""] || "border-neutral-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-4 ${toneClass}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}

function Pill({ tone, children }) {
  const cls = {
    ok:    "border border-emerald-200 bg-emerald-50 text-emerald-800",
    warn:  "border border-amber-300 bg-amber-50 text-amber-900",
    alert: "border border-[#C6851E]/40 bg-[#C6851E]/10 text-[#8A5A12]",
    muted: "border border-neutral-200 bg-neutral-50 text-neutral-600",
  }[tone];
  return <span className={`rounded-full px-2 py-0.5 ${cls}`}>{children}</span>;
}

function ProfileDrawer({ open, onOpenChange, profile, types, certs, onAdd, onEdit, onChanged }) {
  if (!profile) return null;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        data-testid="admin-compliance-drawer"
        side="right"
        className="max-h-[100dvh] w-full overflow-y-auto sm:max-w-lg"
      >
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{profile.full_name || profile.email}</SheetTitle>
          <SheetDescription>Manage certifications and renewal dates.</SheetDescription>
        </SheetHeader>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">
            Certifications · {certs.length}
          </p>
          <button
            type="button"
            data-testid="admin-compliance-add-cert"
            onClick={onAdd}
            className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-[11px] font-semibold text-white hover:bg-primary-hover"
          >
            <Plus size={11} weight="bold" />
            Add cert
          </button>
        </div>
        {certs.length === 0 && (
          <p data-testid="admin-compliance-drawer-empty" className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            No certifications recorded yet.
          </p>
        )}
        <ul className="mt-3 space-y-2">
          {certs.map((c) => (
            <CertRow key={c.id} cert={c} onEdit={() => onEdit(c)} onChanged={onChanged} />
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}

function CertRow({ cert, onEdit, onChanged }) {
  const status = deriveStatus(cert);
  const meta = STATUS_META[status];
  const days = daysUntilExpiry(cert.expires_on);
  const onRevoke = async () => {
    const reason = window.prompt(`Revoke ${cert.cert_type?.label}? Optional reason:`, "");
    if (reason === null) return;
    try {
      await revokeCertification(cert.id, reason);
      toast.success("Certification revoked");
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not revoke");
    }
  };
  const onOpenDoc = async () => {
    const url = await getCertDocumentUrl(cert.document_path);
    if (url) window.open(url, "_blank", "noopener");
    else toast.error("Document not available");
  };
  const onDeleteDoc = async () => {
    if (!window.confirm("Delete attached document?")) return;
    try {
      await deleteCertDocument({ certificationId: cert.id, path: cert.document_path });
      toast.success("Document removed");
      onChanged?.();
    } catch (err) {
      toast.error(err.message || "Could not delete document");
    }
  };
  return (
    <li
      data-testid={`admin-compliance-cert-${cert.id}`}
      data-status={status}
      className="rounded-2xl border border-neutral-200 bg-white p-3"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
          <ShieldCheck size={16} weight="duotone" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-neutral-900">
            {cert.cert_type?.label || "Certification"}
          </p>
          <p className="text-[11px] text-neutral-500">
            Issued {cert.issued_on}
            {cert.expires_on
              ? ` · expires ${cert.expires_on}${days !== null ? ` (${days >= 0 ? `in ${days}d` : `${Math.abs(days)}d ago`})` : ""}`
              : " · no expiry"}
          </p>
          {cert.notes && <p className="mt-0.5 text-[11px] italic text-neutral-500">"{cert.notes}"</p>}
        </div>
        <span className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.tone}`}>
          {meta.label}
        </span>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        <button type="button" data-testid={`admin-compliance-cert-${cert.id}-edit`} onClick={onEdit} className="rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
          Edit
        </button>
        {cert.document_path ? (
          <>
            <button type="button" data-testid={`admin-compliance-cert-${cert.id}-doc`} onClick={onOpenDoc} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">
              <FileText size={11} weight="duotone" /> Open doc
            </button>
            <button type="button" data-testid={`admin-compliance-cert-${cert.id}-doc-del`} onClick={onDeleteDoc} className="rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5">
              Remove doc
            </button>
          </>
        ) : null}
        {!cert.revoked_at && (
          <button type="button" data-testid={`admin-compliance-cert-${cert.id}-revoke`} onClick={onRevoke} className="ml-auto inline-flex items-center gap-1 rounded-full border border-[#C6851E]/40 bg-white px-2.5 py-0.5 text-[11px] font-semibold text-[#8A5A12] hover:bg-[#C6851E]/5">
            <Trash size={11} weight="duotone" /> Revoke
          </button>
        )}
      </div>
    </li>
  );
}

function CertEditorSheet({ open, onOpenChange, cert, profile, types, onSaved }) {
  const isEdit = !!cert;
  const [typeId, setTypeId] = useState("");
  const [issuedOn, setIssuedOn] = useState("");
  const [expiresOn, setExpiresOn] = useState("");
  const [notes, setNotes] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setTypeId(cert?.cert_type_id || types[0]?.id || "");
    setIssuedOn(cert?.issued_on || new Date().toISOString().slice(0, 10));
    setExpiresOn(cert?.expires_on || "");
    setNotes(cert?.notes || "");
    setFile(null);
  }, [open, cert, types]);

  // Auto-suggest expires_on from cert_type's default validity when issued_on changes
  // and the user hasn't manually set one.
  useEffect(() => {
    if (!open || cert) return;  // only suggest on new
    const t = types.find((x) => x.id === typeId);
    if (t?.default_validity_months && issuedOn && !expiresOn) {
      setExpiresOn(suggestExpiryDate(issuedOn, t.default_validity_months));
    }
  }, [typeId, issuedOn, open, cert, types, expiresOn]);

  const onSave = async () => {
    if (!profile?.id) return;
    if (!typeId) { toast.error("Pick a cert type"); return; }
    if (!issuedOn) { toast.error("Pick an issue date"); return; }
    setBusy(true);
    try {
      const saved = await upsertCertification({
        id: cert?.id,
        profileId: profile.id,
        certTypeId: typeId,
        issuedOn,
        expiresOn: expiresOn || null,
        documentPath: cert?.document_path || null,
        notes,
      });
      if (file && saved?.id) {
        await uploadCertDocument({ certificationId: saved.id, file });
      }
      toast.success(isEdit ? "Certification updated" : "Certification recorded");
      onSaved?.();
      onOpenChange?.(false);
    } catch (err) {
      toast.error(err.message || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-testid="admin-compliance-editor" side="bottom" className="max-h-[92dvh] overflow-y-auto rounded-t-3xl sm:max-w-md sm:mx-auto">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading text-xl">{isEdit ? "Edit certification" : "Add certification"}</SheetTitle>
          <SheetDescription>For {profile?.full_name || profile?.email}.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-3">
          <Field label="Type">
            <select data-testid="admin-compliance-editor-type" value={typeId} onChange={(e) => setTypeId(e.target.value)} className={INPUT}>
              {types.map((t) => (<option key={t.id} value={t.id}>{t.label}</option>))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Issued on">
              <input type="date" data-testid="admin-compliance-editor-issued" value={issuedOn} onChange={(e) => setIssuedOn(e.target.value)} className={INPUT} />
            </Field>
            <Field label="Expires on (optional)">
              <input type="date" data-testid="admin-compliance-editor-expires" value={expiresOn} onChange={(e) => setExpiresOn(e.target.value)} className={INPUT} />
            </Field>
          </div>
          <Field label="Notes (optional)">
            <textarea rows={2} data-testid="admin-compliance-editor-notes" value={notes} onChange={(e) => setNotes(e.target.value)} className={INPUT} />
          </Field>
          <Field label="Attach proof (optional, PDF / image)">
            <input
              type="file"
              accept="application/pdf,image/*"
              data-testid="admin-compliance-editor-file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="block w-full text-sm text-neutral-700"
            />
            {cert?.document_path && !file && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-neutral-500">
                <FileArrowUp size={11} weight="duotone" /> Existing doc on file. Picking a new file will replace it after save.
              </p>
            )}
          </Field>
        </div>

        <div className="mt-5 flex flex-wrap items-center justify-end gap-2">
          <button type="button" data-testid="admin-compliance-editor-cancel" onClick={() => onOpenChange?.(false)} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50">
            <X size={12} weight="bold" /> Cancel
          </button>
          <button type="button" data-testid="admin-compliance-editor-save" disabled={busy} onClick={onSave} className="inline-flex items-center gap-1.5 rounded-full bg-primary px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover disabled:opacity-60">
            <ShieldCheck size={12} weight="duotone" />
            {busy ? "Saving…" : isEdit ? "Save changes" : "Record certification"}
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

const INPUT = "w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</span>
      {children}
    </label>
  );
}
